// Entra ID (Azure AD) sign-in — SPA + Auth Code with PKCE via MSAL.
//
// The app registration (Lennar-DigitalRoadMap_Auth) is single-tenant: the
// authority pins our tenant (NOT /common), access is gated by the
// Digital_Roadmap_User group, and the API scope is access_as_user. No client
// secret exists in the SPA — PKCE replaces it by design.
//
// Auth is OFF unless the environment turns it on (AUTH_ENABLED via runtime
// config — see config.ts / docker/40-runtime-config.sh). That lets us deploy
// this code everywhere and flip environments one at a time as gateway JWT
// validation comes online, with no rebuild.

import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  EventType,
  type AuthenticationResult,
} from "@azure/msal-browser";
import { AUTH_ENABLED, AUTH_OVERRIDES } from "./config";

const TENANT_ID = AUTH_OVERRIDES.tenantId || "28e546c7-fd4b-4246-b0c1-c800d6381d7d";
const CLIENT_ID = AUTH_OVERRIDES.clientId || "f56b6f24-7c8d-4c69-b49d-681ae13916c5";
const API_SCOPE =
  AUTH_OVERRIDES.scope || `api://${CLIENT_ID}/access_as_user`;

// Microsoft Graph, for the directory-backed owner picker (lib/directory.ts).
// Delegated + read-only: the signed-in user can look up other users' names,
// emails and titles — the same thing the Outlook address book shows them.
// Consent is granted per app registration (non-prod and PROD separately).
export const GRAPH_SCOPES = ["User.ReadBasic.All"];

// Sign in for the API scope, and ask for Graph consent in the same trip so the
// picker's later silent token request doesn't need an interactive step.
export const LOGIN_REQUEST = { scopes: [API_SCOPE], extraScopesToConsent: GRAPH_SCOPES };

// Singleton — created only when auth is enabled for this environment.
export const msalInstance: PublicClientApplication | null = AUTH_ENABLED
  ? new PublicClientApplication({
      auth: {
        clientId: CLIENT_ID,
        authority: `https://login.microsoftonline.com/${TENANT_ID}`,
        redirectUri: window.location.origin, // registered: dev URL + localhost:3000
        postLogoutRedirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: "sessionStorage", // per-tab; nothing persists after close
      },
    })
  : null;

// Await this before rendering anything that touches MSAL state.
// Set when the login redirect comes back with an error (bad app registration,
// user cancelled, conditional-access denial…). The gate shows it above the
// sign-in button so a failed login lands somewhere readable, not a dead app.
export let authError: string | null = null;

export async function initAuth(): Promise<void> {
  if (!msalInstance) return;
  try {
    await msalInstance.initialize();
    // Returning from the Microsoft login redirect lands here with the code in
    // the URL fragment; this exchanges it for tokens.
    const result = await msalInstance.handleRedirectPromise();
    if (result?.account) {
      msalInstance.setActiveAccount(result.account);
    } else if (!msalInstance.getActiveAccount()) {
      const [first] = msalInstance.getAllAccounts();
      if (first) msalInstance.setActiveAccount(first);
    }
    // Keep the active account current across future logins (e.g. other tabs).
    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        if (payload.account) msalInstance!.setActiveAccount(payload.account);
        resetGraphConsentState(); // a fresh login may carry the consent
      }
    });
  } catch (err) {
    // Never leave the app unrendered: surface the failure on the gate screen.
    authError = err instanceof Error ? err.message : String(err);
    console.error("Entra sign-in failed:", err);
  }
}

// True when auth is on and someone is signed in, i.e. API calls carry a token.
export function isSignedIn(): boolean {
  return !!msalInstance?.getActiveAccount();
}

// Access token for API calls. Null when auth is off or nobody is signed in
// (the gate keeps the app unmounted until sign-in, so null mid-session means
// the session expired — we bounce through the redirect to renew).
// forceRefresh skips MSAL's cache: used when the gateway rejected the cached token.
export async function getAccessToken({ forceRefresh = false } = {}): Promise<string | null> {
  if (!msalInstance) return null;
  const account = msalInstance.getActiveAccount();
  if (!account) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({
      ...LOGIN_REQUEST,
      account,
      forceRefresh,
    });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Refresh token expired/revoked — full page redirect, comes back signed in.
      await msalInstance.acquireTokenRedirect({ ...LOGIN_REQUEST, account });
    }
    return null;
  }
}

// Access token for Microsoft Graph (a different resource than our API, so it
// is a separate token). This is the SILENT path only — it never opens UI.
//
//   ok              token in hand
//   consent-needed  the session predates the Graph scope (or consent was never
//                   granted): an interactive step is required. The picker shows
//                   a "Connect directory" button that calls requestGraphConsent
//                   from the click — a popup opened from a debounce timer has no
//                   user gesture behind it and browsers block it.
//   unavailable     auth is off, nobody is signed in, the user already refused
//                   the consent popup this page load, or a transient failure.
export type GraphTokenResult =
  | { status: "ok"; token: string }
  | { status: "consent-needed" }
  | { status: "unavailable" };

// Set only when the user explicitly dismissed the consent popup, so typing in
// the picker can't keep re-offering it. Cleared on sign-out and re-login. A
// blocked popup or a network blip is NOT a refusal and is retried next time.
let graphDenied = false;
let graphInFlight: Promise<GraphTokenResult> | null = null;

export function resetGraphConsentState(): void {
  graphDenied = false;
}

export function getGraphToken(): Promise<GraphTokenResult> {
  if (!msalInstance) return Promise.resolve({ status: "unavailable" });
  if (graphDenied) return Promise.resolve({ status: "unavailable" });
  const account = msalInstance.getActiveAccount();
  if (!account) return Promise.resolve({ status: "unavailable" });
  if (graphInFlight) return graphInFlight;
  const instance = msalInstance;
  graphInFlight = (async (): Promise<GraphTokenResult> => {
    try {
      const result = await instance.acquireTokenSilent({ scopes: GRAPH_SCOPES, account });
      return { status: "ok", token: result.accessToken };
    } catch (err) {
      return err instanceof InteractionRequiredAuthError
        ? { status: "consent-needed" }
        : { status: "unavailable" };
    } finally {
      graphInFlight = null;
    }
  })();
  return graphInFlight;
}

// Interactive consent — call this synchronously from a user gesture (a click),
// never from a timer or a promise chain, or the browser's popup blocker wins.
// Returns the token, or null if the user closed the popup / it was blocked.
export async function requestGraphConsent(): Promise<string | null> {
  if (!msalInstance) return null;
  const account = msalInstance.getActiveAccount();
  if (!account) return null;
  try {
    const result = await msalInstance.acquireTokenPopup({ scopes: GRAPH_SCOPES, account });
    return result.accessToken;
  } catch (err) {
    // MSAL reports an explicit dismissal as errorCode "user_cancelled".
    const code = (err as { errorCode?: string })?.errorCode;
    if (code === "user_cancelled") graphDenied = true;
    return null;
  }
}

// Full sign-in round trip for the current account, from the load error
// screen's "Sign in again". Comes back to the app with a fresh token.
export function renewSignIn(): void {
  if (!msalInstance) return;
  const account = msalInstance.getActiveAccount() ?? undefined;
  void msalInstance.acquireTokenRedirect({ ...LOGIN_REQUEST, account });
}

export function signOut(): void {
  resetGraphConsentState();
  void msalInstance?.logoutRedirect();
}

// ---------- who is signed in ----------
//
// Identity comes from the Entra token, not from anything the user can type —
// so update authorship can't be spoofed by picking someone else's name, and
// people records carry official spellings. `oid` is Entra's immutable object
// id: emails and display names change, that doesn't.

export type CurrentUser = {
  oid: string;
  name: string;
  email: string | null;
};

// The claim-mapping rules, pure and testable (getCurrentUser just supplies the
// live MSAL account).
export function mapIdentity(account: {
  name?: string;
  username?: string;
  localAccountId: string;
  idTokenClaims?: Record<string, unknown>;
}): CurrentUser {
  const claims = account.idTokenClaims ?? {};
  const oid = typeof claims.oid === "string" ? claims.oid : account.localAccountId;
  const email =
    (typeof claims.email === "string" && claims.email) ||
    (typeof claims.preferred_username === "string" && claims.preferred_username) ||
    account.username ||
    null;
  return {
    oid,
    // Prefer the directory display name; fall back to the email local-part so
    // we never render an empty author.
    name: account.name?.trim() || (email ? email.split("@")[0] : "Unknown user"),
    email: email && email.includes("@") ? email.toLowerCase() : null,
  };
}

export function getCurrentUser(): CurrentUser | null {
  const account = msalInstance?.getActiveAccount();
  if (!account) return null;
  return mapIdentity({
    name: account.name,
    username: account.username,
    localAccountId: account.localAccountId,
    idTokenClaims: account.idTokenClaims as Record<string, unknown> | undefined,
  });
}

// Find the signed-in user's roster entry.
//
// Matched on email first — that's the identity Entra gives us and what the API
// dedupes on — with a name fallback for rows created by hand before anyone had
// signed in. Deliberately NOT matched on the client-generated id: when the API
// reuses an existing row it returns its own id, which would leave a stale
// reference and make a successful write look like a failed one.
export function matchRosterPerson<T extends { name: string; email?: string | null }>(
  identity: CurrentUser | null,
  roster: readonly T[]
): T | null {
  if (!identity) return null;
  if (identity.email) {
    const byEmail = roster.find(p => p.email?.toLowerCase() === identity.email);
    if (byEmail) return byEmail;
  }
  const target = identity.name.trim().toLowerCase();
  return roster.find(p => p.name.trim().toLowerCase() === target) ?? null;
}
